// Recordings, /cashflow/recordings (PCFL v3's Recordings, reference/pcfl-v3/source/app.jsx, in the
// hub's frame; no board): every recording by module, with search across the titles and chapters and
// a chip per module. Each module has its side of the pentagon and its homework count; each recording
// its thumbnail, where they are in it and how many lessons it has. A chapter found opens the
// recording at that minute. With 1 module picked, its homework sits under its recordings. A recording
// with Adam's own thumbnail (decision 47, 12 Oct) shows it in place of the green panel, every 1 on the
// page signed in 1 call.
import { useState } from 'react'
import { Link } from '../../lib/router.jsx'
import { chapterTime, linkStamp } from '../../lib/timestamp.js'
import { useProductData } from '../../product/ProductData.jsx'
import { COPY } from '../../products/ondemand/copy.js'
import { ModuleMark } from '../../products/ondemand/Pentagon.jsx'
import { Chip, Homework, PageHead, Thumb } from '../../products/ondemand/parts.jsx'
import {
  chapterName, courseModel, homeworkCount, lessons, recordingHref, recordingStatus, searchRows,
} from '../../products/ondemand/model.js'
import { thumbItem } from '../../product/recordingThumbs.js'
import { useRecordingThumbs } from '../../product/RecordingThumb.jsx'
import '../../styles/ondemand.css'

export default function OnDemandRecordings({ product }) {
  const { data } = useProductData()
  const [term, setTerm] = useState('')
  const [pick, setPick] = useState('all')
  const model = courseModel(data, product)
  const found = searchRows(model.recs, term)
  const shownMods = model.modules.filter(m => pick === 'all' || m.id === pick)
  // a recording's picture only once it's open to them (row.ready, Adam, 12 Oct); the rest keep the panel
  const thumbs = useRecordingThumbs(model.recs.map(r => ({ item: thumbItem(r, r.ready) })))
  let shown = 0

  return (
    <div className="od od-recs">
      <PageHead kicker={COPY.recKicker} title={COPY.recTitle} lede={COPY.recLede} />
      <div className="od-tools">
        <label className="od-search">
          <svg className="icon-line" width="17" height="17" viewBox="0 0 24 24" aria-hidden="true" style={{ strokeWidth: 2 }}>
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
          </svg>
          <input type="search" value={term} onChange={e => setTerm(e.target.value)} placeholder={COPY.recSearch} aria-label={COPY.recSearchLabel} />
        </label>
        <div className="od-chips" role="group" aria-label={COPY.recKicker}>
          <button type="button" className={pick === 'all' ? 'od-filter od-filter--on' : 'od-filter'} aria-pressed={pick === 'all'} onClick={() => setPick('all')}>{COPY.all}</button>
          {model.modules.map(m => (
            <button key={m.id} type="button" className={pick === m.id ? 'od-filter od-filter--on' : 'od-filter'} aria-pressed={pick === m.id}
              onClick={() => setPick(m.id)}>
              {m.number == null ? m.label : `${m.number} ${m.title}`}
            </button>
          ))}
        </div>
      </div>

      {shownMods.map(m => {
        const rows = found.filter(x => x.row.module_id === m.id)
        if (!rows.length) return null
        shown += rows.length
        const index = model.numbered.findIndex(x => x.id === m.id)
        const state = index >= 0 ? model.states[index] : 'todo'
        const hw = homeworkCount(data, m.id)
        return (
          <section key={m.id} className="od-mod" aria-labelledby={`od-mod-${m.key}`}>
            <div className="od-mod-h">
              <ModuleMark index={index} state={state} size={34} pentagon={model.pentagon} />
              <div className="od-mod-titles">
                <span className="od-mod-no">{m.number == null ? (model.firstNumbered ? COPY.beforeFirst(model.firstNumbered.label) : '') : m.label}</span>
                <h2 className="od-mod-name" id={`od-mod-${m.key}`}>{m.number == null ? m.label : m.title}</h2>
              </div>
              {hw.total > 0 && (
                <span className="od-mod-right">
                  {hw.done >= hw.total ? <Chip tone="done">{COPY.homeworkChipDone}</Chip> : <Chip>{COPY.homeworkChip(hw.done, hw.total)}</Chip>}
                </span>
              )}
            </div>
            <div className="od-rec-list">
              {rows.map(({ row, hits }) => {
                const status = recordingStatus(row)
                const href = row.ready ? recordingHref(product, data, row) : null
                const to = href && hits.length ? `${href}?t=${linkStamp(hits[0].start_seconds)}` : href
                const heading = row.part ? `Part ${row.part}: ${row.title}` : (m.teaser || row.title)
                const inner = (
                  <>
                    <Thumb row={row} index={index} state={state} thumbs={thumbs} pentagon={model.pentagon} />
                    <span className="od-rec-text">
                      <span className="od-rec-meta"><Chip tone={status.tone}>{status.text}</Chip><span>{lessons(row.chapters.length)}</span></span>
                      <span className="od-rec-h">{heading}</span>
                      {hits.length > 0 && (
                        <span className="od-rec-hits">
                          {hits.map(c => (
                            <span key={c.start_seconds}><b>{chapterName(c)}</b>{row.ready ? COPY.hitAt(chapterTime(c.start_seconds)) : ''}</span>
                          ))}
                        </span>
                      )}
                    </span>
                  </>
                )
                return to
                  ? <Link key={row.id} to={to} className="od-card od-rec">{inner}</Link>
                  : <div key={row.id} className="od-rec od-rec--locked">{inner}</div>
              })}
            </div>
            {pick === m.id && <div className="od-card od-pad od-mod-hw"><Homework module={m} /></div>}
          </section>
        )
      })}
      {shown === 0 && <p className="od-empty">{term.trim() ? COPY.noMatch(term.trim()) : COPY.noRecordings}</p>}
    </div>
  )
}
